import { Alert, Badge, Button, EmptyState, FieldBlock, Input, LoadingState, TagSelect, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { CatalogIcon, IconField } from '../../components/IconPicker.tsx'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { ColorField, SwitchCard, normalizarCodigo } from './catalog-shared.tsx'

/**
 * Cadastro de Tipos de Item de Trabalho no padrao FormScreen
 * (SPEC-20260815-0821) — antes vivia no AdminPage com o modal generico.
 * Mantem o seletor de icones (biblioteca, upload, busca na web) e a cor que
 * pinta o icone nas listas. Nao ha mais "tipo do sistema" (2026-08-30): todos
 * sao cadastraveis; a API so impede excluir o que esta EM USO.
 */

type TypeList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.catalog)['work-item-types']['$get']>>['json']>>
type TypeRow = TypeList['types'][number]

type FormTipo = {
  namePt: string
  nameEn: string
  code: string
  icon: string
  iconImageId: string | null
  color: string | null
  isActive: boolean
  sortOrder: number
  /** Fluxos a que o tipo pertence (N:N) — editável na própria tela do tipo. */
  flowIds: string[]
}

const VAZIO: FormTipo = {
  namePt: '',
  nameEn: '',
  code: '',
  icon: '',
  iconImageId: null,
  color: null,
  isActive: true,
  sortOrder: 0,
  flowIds: [],
}

export function WorkItemTypesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()

  const lista = useQuery({
    queryKey: ['work-item-types'],
    queryFn: async () => (await api.catalog['work-item-types'].$get()).json(),
  })
  // fluxos do workspace: alimentam o seletor "Fluxos" do tipo
  const fluxos = useQuery({ queryKey: ['flows'], queryFn: async () => (await api.flows.$get()).json() })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormTipo>(VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) abrir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  /**
   * Invalida lista, bootstrap E o bundle de traducoes: o seletor de tipo do
   * Item de Trabalho depende do bundle para mostrar o titulo novo.
   */
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['work-item-types'] })
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    void qc.invalidateQueries({ queryKey: ['i18n'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const corpoDe = (f: FormTipo) => ({
    code: normalizarCodigo(f.code || f.namePt),
    name: { 'pt-BR': f.namePt.trim(), ...(f.nameEn.trim() ? { 'en-US': f.nameEn.trim() } : {}) },
    icon: f.icon.trim() || null,
    iconImageId: f.iconImageId,
    color: f.color,
    isActive: f.isActive,
    sortOrder: f.sortOrder,
    flowIds: f.flowIds,
  })

  const criar = useMutation({
    mutationFn: async (f: FormTipo) => (await api.catalog['work-item-types'].$post({ json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Tipo criado.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormTipo }) =>
      (await api.catalog['work-item-types'][':id'].$patch({ param: { id }, json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Tipo atualizado.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog['work-item-types'][':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.catalog['work-item-types'][':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Tipo excluído.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const tipos = lista.data?.types ?? []
  const emEdicao = editando?.id ? tipos.find((t) => t.id === editando.id) : undefined

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return tipos
    return tipos.filter((t) => `${t.name['pt-BR'] ?? ''} ${t.code}`.toLowerCase().includes(termo))
  }, [tipos, busca])

  const abrir = (alvo: TypeRow | null) => {
    setForm(
      alvo
        ? {
            namePt: alvo.name['pt-BR'] ?? '',
            nameEn: alvo.name['en-US'] ?? '',
            code: alvo.code,
            icon: alvo.icon ?? '',
            iconImageId: alvo.iconImageId,
            color: alvo.color,
            isActive: alvo.isActive,
            sortOrder: alvo.sortOrder,
            flowIds: (alvo as { flowIds?: string[] }).flowIds ?? [],
          }
        : { ...VAZIO, sortOrder: (tipos.at(-1)?.sortOrder ?? 0) + 10 },
    )
    setEditando({ id: alvo?.id ?? null })
  }

  const alterado =
    editando?.id == null
      ? form.namePt.trim().length >= 2
      : emEdicao !== undefined &&
        JSON.stringify(corpoDe(form)) !==
          JSON.stringify(
            corpoDe({
              namePt: emEdicao.name['pt-BR'] ?? '',
              nameEn: emEdicao.name['en-US'] ?? '',
              code: emEdicao.code,
              icon: emEdicao.icon ?? '',
              iconImageId: emEdicao.iconImageId,
              color: emEdicao.color,
              isActive: emEdicao.isActive,
              sortOrder: emEdicao.sortOrder,
              flowIds: (emEdicao as { flowIds?: string[] }).flowIds ?? [],
            }),
          )

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const siglaDerivada = normalizarCodigo(form.code || form.namePt)
    const salvarValido = alterado && form.namePt.trim().length >= 2 && !criar.isPending && !atualizar.isPending

    return (
      <FormScreen
        title={editando.id ? 'Editar tipo de item' : 'Novo tipo de item'}
        subtitle={editando.id ? (emEdicao?.name['pt-BR'] ?? emEdicao?.code) : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar tipo'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        // Exclusão no rodapé, à esquerda (padrão 2026-08-30). Só em edição, e
        // bloqueada pelas mesmas regras da lista: tipo do sistema ou em uso.
        onDelete={
          emEdicao
            ? () => {
                const nome = emEdicao.name['pt-BR'] ?? emEdicao.code
                if (!confirm(`Excluir o tipo “${nome}”?`)) return
                excluir.mutate(emEdicao.id, { onSuccess: () => setEditando(null) })
              }
            : undefined
        }
        deleteLabel="Excluir tipo de item"
        deleteDisabled={(emEdicao?.usageCount ?? 0) > 0}
        deleteHint={(emEdicao?.usageCount ?? 0) > 0 ? 'Tipo em uso: desative-o para tirá-lo das opções' : undefined}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações do tipo">
          <FormRow cols="2fr 2fr 1fr">
            <FieldBlock label="Nome do tipo" required hint="Como aparece nas telas em português.">
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.namePt}
                  placeholder="Ex.: Tarefa técnica"
                  onChange={(e) => setForm((f) => ({ ...f, namePt: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Name (en-US)" hint="Opcional — para quem usa a plataforma em inglês.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.nameEn}
                  placeholder="Ex.: Technical task"
                  onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock
              label="Código"
              hint={
                form.code.trim() === '' && form.namePt.trim() !== ''
                    ? `Derivado do nome: ${siglaDerivada}. Digite para personalizar.`
                    : 'Identificador técnico, único no workspace.'
              }
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={form.code}

                  placeholder={siglaDerivada || 'Ex.: TASK'}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, code: f.code ? normalizarCodigo(f.code) : f.code }))}
                />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        {/* fluxos do TIPO (N:N): mostra em quais fluxos ele está e permite
            incluir/remover daqui mesmo (pedido 2026-08-29) */}
        <FormSection title={`Fluxos${form.flowIds.length > 0 ? ` (${form.flowIds.length})` : ''}`}>
          <FieldBlock
            label="Fluxos em que este tipo está incluído"
            hint="Itens deste tipo seguem os fluxos escolhidos. Um tipo pode pertencer a mais de um fluxo — ao criar o item você escolhe por qual começar. Sem fluxo, o tipo não segue nenhum processo."
          >
            {({ id }) => (
              <TagSelect
                id={id}
                value={form.flowIds}
                placeholder="Escolha os fluxos…"
                emptyLabel="Nenhum fluxo — o tipo não segue processo"
                options={(fluxos.data?.flows ?? []).map((fx) => ({ value: fx.id, label: fx.name }))}
                onChange={(v) => setForm((f) => ({ ...f, flowIds: v }))}
              />
            )}
          </FieldBlock>
        </FormSection>

        <FormSection title="Aparência">
          <FormRow cols="1fr 1fr">
            <IconField
              value={form.icon}
              imageId={form.iconImageId}
              color={form.color ?? undefined}
              onChange={(escolha) => setForm((f) => ({ ...f, icon: escolha.icon, iconImageId: escolha.iconImageId }))}
            />
            <ColorField
              hint="Pinta o ícone nas listas — o nome continua sendo o que comunica."
              value={form.color}
              onChange={(color) => setForm((f) => ({ ...f, color }))}
            />
          </FormRow>

          <FormRow cols="1fr minmax(220px, 420px)">
            <FieldBlock label="Ordem" hint="Menor aparece primeiro no seletor de tipos.">
              {({ id }) => (
                <Input
                  id={id}
                  type="number"
                  min={0}
                  value={String(form.sortOrder)}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Math.max(0, Number(e.target.value) || 0) }))}
                />
              )}
            </FieldBlock>
            <SwitchCard
              title={form.isActive ? 'Tipo ativo' : 'Tipo inativo'}
              description={form.isActive ? 'Aparece ao criar Itens de Trabalho e nos quadros.' : 'Some das opções; itens existentes ficam.'}
              checked={form.isActive}
              onChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
            />
          </FormRow>

          {emEdicao ? (
            <p className="adm-hint">
              <i className="fas fa-link" aria-hidden />{' '}
              {emEdicao.usageCount > 0
                ? `Em uso por ${emEdicao.usageCount} ${emEdicao.usageCount === 1 ? 'Item de Trabalho' : 'Itens de Trabalho'}.`
                : 'Ainda sem uso.'}
              {emEdicao.code === 'ACCEPTANCE_CRITERION'
                ? ' Âncora estrutural: itens deste tipo funcionam como critérios de aceite.'
                : ''}
            </p>
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Tipos de Item de Trabalho</h1>
        <p className="adm-head__desc">Catálogo dos tipos — ícone e cor identificam o item nos quadros e nas listas.</p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando os tipos" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os tipos"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome ou código…" aria-label="Filtrar tipos"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {tipos.length} {tipos.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-shapes"
            title={tipos.length === 0 ? 'Nenhum tipo ainda' : 'Nenhum tipo com esse filtro'}
            description={tipos.length === 0
              ? 'Crie o primeiro tipo para poder cadastrar Itens de Trabalho.'
              : `Nada casa com “${busca}”.`}
            action={tipos.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Novo tipo</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtrados.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const nome = row.name['pt-BR'] ?? row.code
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon" style={row.color ? { color: row.color } : undefined}>
                  <CatalogIcon icon={row.iconImageUrl ?? row.icon ?? 'fas fa-shapes'} />
                </span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{nome}</span>
                  <span className="adm-catrow__sub">
                    Código {row.code}
                    {row.usageCount > 0 ? ` · em uso por ${row.usageCount} ${row.usageCount === 1 ? 'item' : 'itens'}` : ' · sem uso'}
                  </span>
                </span>

                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{nome}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativo' : 'Inativo'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${nome} está ativo`}
                        title={row.isActive ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${nome}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger"
                      disabled={row.usageCount > 0}
                      aria-label={row.usageCount > 0 ? `${row.code} está em uso` : `Excluir ${row.code}`}
                      title={row.usageCount > 0 ? 'Tipo em uso: desative-o para tirá-lo das opções' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
